/**
 * Table aliases for the SQL editor's completions (owner, 30.09.2026: picking
 * `fe_users` after FROM should insert `fe_users fu`, as DataGrip and DBeaver
 * do — initials, numbered on a clash).
 *
 * Also the table-reference parser the completions resolve `alias.` with. It
 * now reads ONE statement (the one under the cursor) instead of the whole
 * script — the first matching alias anywhere in the document used to win —
 * and understands comma joins (`FROM a x, b y`).
 */

// Words that can follow a table reference but are never its alias.
export const NON_ALIAS_WORDS = new Set([
  'where', 'on', 'set', 'join', 'inner', 'left', 'right', 'outer', 'cross', 'group',
  'order', 'limit', 'offset', 'having', 'union', 'values', 'select', 'from', 'as', 'using',
  'natural', 'full', 'window', 'returning', 'for', 'lateral',
]);

// Short words an alias must never be: SQL keywords (a bare `as`, `in`, `on`…
// would make the statement mean something else) plus the parser's stop words.
const RESERVED = new Set([
  'as', 'at', 'by', 'do', 'if', 'in', 'is', 'of', 'on', 'or', 'to', 'all', 'and', 'any', 'asc',
  'desc', 'end', 'for', 'key', 'not', 'set', 'use', 'add', 'div', 'mod', 'xor', 'row', 'top',
  'case', 'else', 'from', 'into', 'join', 'like', 'null', 'then', 'true', 'when', 'with',
  ...NON_ALIAS_WORDS,
]);

/** `fe_users` → `fu`, `orderItems` → `oi`, `"public"."users"` → `u`; numbered when taken or reserved. */
export function suggestTableAlias(table, usedNames = []) {
  const bare = String(table || '').replace(/[`"[\]]/g, '').split('.').pop();
  const parts = bare
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  let base = parts.map((p) => p[0]).join('').toLowerCase() || 't';
  if (/^[0-9]/.test(base)) base = `t${base}`;
  const used = new Set([...usedNames].map((n) => String(n).toLowerCase()));
  let alias = base;
  for (let n = 1; RESERVED.has(alias) || used.has(alias); n += 1) alias = `${base}${n}`;
  return alias;
}

const IDENT = '[`"]?([A-Za-z_][\\w$]*(?:\\.[A-Za-z_][\\w$]*)?)[`"]?';
const REF = new RegExp(`^\\s*${IDENT}(?:\\s+(?:as\\s+)?[\`"]?([A-Za-z_][\\w$]*)[\`"]?)?`, 'i');

function refAt(text) {
  const m = text.match(REF);
  if (!m) return null;
  const table = m[1].includes('.') ? m[1].split('.').pop() : m[1];
  let alias = m[2] || null;
  if (alias && NON_ALIAS_WORDS.has(alias.toLowerCase())) alias = null;
  return { table, alias, length: m[0].length };
}

/**
 * `FROM users u JOIN orders AS o, items i` → users/u, orders/o, items/i.
 * UPDATE and INSERT INTO targets count too (no comma lists there).
 */
export function parseTableRefs(text) {
  const src = String(text || '');
  const refs = [];
  const kw = /\b(from|join|update|into)\s+/gi;
  let m;
  while ((m = kw.exec(src))) {
    let rest = src.slice(m.index + m[0].length);
    if (/^\(/.test(rest)) continue; // a subquery, not a table
    let ref = refAt(rest);
    while (ref) {
      refs.push({ table: ref.table, alias: ref.alias });
      rest = rest.slice(ref.length);
      // Comma joins continue only inside FROM.
      const comma = m[1].toLowerCase() === 'from' && rest.match(/^\s*,\s*/);
      if (!comma) break;
      rest = rest.slice(comma[0].length);
      ref = /^\(/.test(rest) ? null : refAt(rest);
    }
  }
  return refs;
}

/** Names already in use in a statement: its table names and aliases. */
export function namesInUse(refs) {
  const out = [];
  for (const r of refs) {
    out.push(r.table);
    if (r.alias) out.push(r.alias);
  }
  return out;
}
